import { _ } from "libs";
import React, { FC, memo, useContext, useEffect, useMemo, useRef, useState } from "react";
import PaymentModal from "../paymentModal";
import Progressbar from "./progressbar";
import ChampionshipContext from "../../contexts/championshipContext";

const ChampionshipTrial: FC = () => {
	const { championship, context } = useContext(ChampionshipContext);

	const [contextLoaded, setContextLoaded] = useState(false);
	const [optInFee, setOptInFee] = useState(championship.settings.optInFee ?? 0);
	const [trialContext, setTrialContext] = useState(null as any);

	if (!championship) {
		return null;
	}

	const hasActiveOrExpiredTrial = (): boolean => {
		return championship?.settings.trial?.hasActiveTrial || championship?.settings.trial?.hasExpiredTrial;
	};

	const contextResult = useMemo(() => {
		//we are checking for promos here
		if (championship.settings.optInFee === 0) {
			setContextLoaded(true);
			return Promise.resolve(null);
		}

		if (championship.optedIn && !hasActiveOrExpiredTrial()) {
			setContextLoaded(true);
			return Promise.resolve(null);
		}

		if (context) {
			setContextLoaded(false);
			return context(null).then((c: any) => {
				setContextLoaded(true);
				calculateOptInFee(c);
				setTrialContext(c?.trial);
				return c;
			});
		} else {
			setContextLoaded(true);
			return Promise.resolve(null);
		}
	}, [championship]);

	const calculateOptInFee = (context) => {
		if (!contextLoaded) {
			setOptInFee(championship.settings.optInFee);
		}

		if (context?.promotions?.[0]?.discount) {
			setOptInFee(championship.settings.optInFee - context.promotions[0].discount);
		} else {
			setOptInFee(championship.settings.optInFee);
		}
	};

	return hasActiveOrExpiredTrial() ? (
		<Trial contextLoaded={contextLoaded} trialContext={trialContext} contextResult={contextResult} />
	) : null;
};

const Trial = ({
	contextLoaded,
	trialContext,
	contextResult,
}: {
	contextLoaded: boolean;
	trialContext;
	contextResult;
}) => {
	const { championship, modal, onPayClicked, onJoinClick, context } = useContext(ChampionshipContext);

	const now: number = (Date.now() / 1000) | 0;
	const totalDays = Math.floor(
		(championship.settings.trial?.trialExpiresAt - championship.optedInAt) / (60 * 60 * 24)
	);
	const daysLeft = Math.floor((championship.settings.trial?.trialExpiresAt - now) / (60 * 60 * 24));
	return (
		<div className="nBlock trial">
			<div className="image" />
			<div className="title">{_("championship.trial.heading")}</div>
			{championship.settings.trial?.hasActiveTrial ? (
				<>
					<div className="progressBarLabel mt-3">
						<div className="icon small-clock-fill-orange" />
						<div className="label">{_("championship.trial.daysRemaining", null, { days: daysLeft })}</div>
					</div>
					<Progressbar current={totalDays - daysLeft} max={totalDays} className="mb-4" />
				</>
			) : null}
			{championship.settings.trial?.hasExpiredTrial ? (
				<div className="progressBarLabel my-3">
					<div className="icon small-alert-fill-red" />
					<div className="label color-red">{_("championship.trial.expired")}</div>
				</div>
			) : null}
			{contextLoaded && trialContext && (
				<>
					<p>{trialContext?.expiryInformationText}</p>
					<p>{trialContext?.upgradePriceInformationText}</p>
				</>
			)}

			<button className="button w-full mt-4" onClick={() => onJoinClick()}>
				{_("championship.trial.purchaseSeasonBtn")}
			</button>
			{championship.settings.optInFee > 0 ? (
				<PaymentModal
					ref={modal}
					description={championship.name}
					amount={championship.settings.optInFee as number}
					currency={championship.settings.currency}
					onPayClicked={onPayClicked}
					context={context}
					precalculatedContext={contextResult}
				/>
			) : null}
		</div>
	);
};

export default memo(ChampionshipTrial);
